import { useCurrentUser } from '@cma/app'
import {
  Avatar,
  Button,
  classNames,
  FormField,
  Input,
  Tooltip
} from '@cma/common'
import { format } from 'date-fns'
import { HelixIcon } from '@helix/helix-icon'
import {
  camera as CameraIcon,
  x_small as XSmallIcon
} from '@helix/helix-icon/outlined'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import AdminImageUploader from './AdminImageUploader'
import { useBrandHub } from './BrandHubContext'
import { ColorPicker } from './ColorPicker'
import { FontSelect } from './FontSelect'
import { BrandingFormData } from './types'
import { useCreateBranding } from './useCreateBranding'
import { useUpdateBranding } from './useUpdateBranding'
import { useFontOptions } from './useFontOptions'
import {
  getValidationRules,
  getInitialFormValues,
  createBrandingInput,
  createNewBrandingInput,
  useAutoHideMessage,
  VALIDATION_RULES,
  DEFAULT_FORM_VALUES,
  brandingToFormData,
  processImagesWithCropping
} from './utils'
import { BrandingWithMeta } from './AdminBrandDetails'

interface BrandAssetsFormProps {
  isDisabled?: boolean
  hasBrandPermissions?: boolean
  existingBranding?: BrandingWithMeta | null
}

export default function BrandAssetsForm({
  isDisabled,
  hasBrandPermissions = true,
  existingBranding
}: BrandAssetsFormProps) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const createBrandingMutation = useCreateBranding()
  const updateBrandingMutation = useUpdateBranding()
  const fontOptions = useFontOptions()
  const {
    selectedEntity,
    setShowAddBrandForm,
    platformClient,
    hasOfficePermissionForSelected
  } = useBrandHub()

  const isFormEditable =
    hasBrandPermissions &&
    hasOfficePermissionForSelected &&
    existingBranding?.editable !== false
  const validationRules = getValidationRules(!!existingBranding?.logoUrl)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors: formErrors },
    reset,
    setError,
    clearErrors
  } = useForm<BrandingFormData>({
    defaultValues: getInitialFormValues(existingBranding),
    mode: 'onChange'
  })

  const [hasFormBeenModified, setHasFormBeenModified] = useState(false)
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [photoPreview, setPhotoPreview] = useState(
    existingBranding?.logoUrl || ''
  )
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [secondaryPhotoPreview, setSecondaryPhotoPreview] = useState(
    existingBranding?.secondaryLogoUrl || ''
  )
  const [secondaryLogoFile, setSecondaryLogoFile] = useState<File | null>(null)
  const [secondaryLogoRemoved, setSecondaryLogoRemoved] = useState(false)
  const [primaryLogoRemoved, setPrimaryLogoRemoved] = useState(false)

  const formData = watch()

  useEffect(() => {
    if (existingBranding) {
      reset({ ...DEFAULT_FORM_VALUES, ...brandingToFormData(existingBranding) })
    } else {
      reset(DEFAULT_FORM_VALUES)
    }
    setHasFormBeenModified(false)
  }, [
    selectedEntity?.id,
    selectedEntity?.brandableType,
    existingBranding?.id,
    existingBranding,
    reset
  ])

  useEffect(() => {
    setHasFormBeenModified(false)
    setSuccessMessage('')
    setApiErrors({})
    clearErrors()
  }, [selectedEntity?.id, clearErrors])

  useEffect(() => {
    const subscription = watch(() => setHasFormBeenModified(true))
    return () => subscription.unsubscribe()
  }, [watch])

  useEffect(() => {
    setPhotoPreview(existingBranding?.logoUrl || '')
    setSecondaryPhotoPreview(existingBranding?.secondaryLogoUrl || '')
    setLogoFile(null)
    setSecondaryLogoFile(null)
    setSecondaryLogoRemoved(false)
    setPrimaryLogoRemoved(false)
  }, [selectedEntity, existingBranding])

  useAutoHideMessage(successMessage, setSuccessMessage)
  useAutoHideMessage(apiErrors.general || '', () => setApiErrors({}))

  const handleLogoChange = (
    file: File | null,
    crop: string | undefined,
    preview: string,
    isSecondary = false
  ) => {
    if (isSecondary) {
      setSecondaryLogoFile(file)
      setSecondaryPhotoPreview(preview)
      setSecondaryLogoRemoved(false)
      if (file) {
        setValue('secondaryLogoContentType', file.type)
        setValue('secondaryLogoFileName', file.name)
        setValue('secondaryLogoCropData', crop)
      }
      setValue('secondaryLogoFile', null)
      setValue('secondaryLogoFile', file)
    } else {
      setLogoFile(file)
      setPhotoPreview(preview)
      setPrimaryLogoRemoved(false)
      clearErrors('logoFile')
      if (file) {
        setValue('logoContentType', file.type)
        setValue('logoFileName', file.name)
        setValue('logoCropData', crop)
      }
      setValue('logoFile', null)
      setValue('logoFile', file)
    }
  }

  const handleRemovePrimaryLogo = () => {
    setLogoFile(null)
    setPhotoPreview('')
    setPrimaryLogoRemoved(true)
    setValue('logoContentType', '')
    setValue('logoFileName', '')
    setValue('logoFile', null)
    setValue('logoCropData', undefined)
  }

  const handleRemoveSecondaryLogo = () => {
    setSecondaryLogoFile(null)
    setSecondaryPhotoPreview('')
    setSecondaryLogoRemoved(true)
    setValue('secondaryLogoContentType', '')
    setValue('secondaryLogoFileName', '')
    setValue('secondaryLogoFile', null)
    setValue('secondaryLogoCropData', undefined)
  }

  const renderRemoveButton = (isSecondary: boolean, previewSrc: string) => {
    if (!previewSrc || !isFormEditable) return null

    const handleRemove = isSecondary
      ? handleRemoveSecondaryLogo
      : handleRemovePrimaryLogo
    const title = `Remove ${isSecondary ? 'Secondary' : 'Primary'} Logo`

    return (
      <button
        type="button"
        onClick={handleRemove}
        className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-gray-600 bg-opacity-80 text-xs font-bold text-white opacity-0 shadow-lg transition-opacity duration-200 hover:bg-gray-700 group-hover:opacity-100"
        title={title}>
        <HelixIcon
          onClick={handleRemove}
          icon={XSmallIcon}
          className="helix-svg-fill--white"
        />
      </button>
    )
  }

  const renderLogoUploader = (
    name: 'logoFile' | 'secondaryLogoFile',
    label: string,
    previewSrc: string,
    isRequired = false,
    isSecondary = false
  ) => (
    <FormField
      required={isRequired}
      label={label}
      error={formErrors[name]?.message}
      className="flex-1">
      <Controller
        name={name}
        control={control}
        rules={validationRules[name]}
        render={({ field }) => (
          <div className="group relative">
            <AdminImageUploader
              key={existingBranding?.id}
              layout="inline"
              rounded={false}
              previewSrc={previewSrc}
              disabled={!isFormEditable}
              onChange={(file, crop, preview) => {
                handleLogoChange(file, crop, preview, isSecondary)
                field.onChange(file)
              }}
              renderPreview={(previewUrl) => (
                <div className="relative">
                  <Avatar
                    size="4xl"
                    shadow
                    rounded={false}
                    src={previewUrl}
                    name={currentUser?.name ?? ''}
                  />
                  {renderRemoveButton(isSecondary, previewSrc)}
                </div>
              )}
              renderTrigger={({ onClick }) => (
                <button
                  type="button"
                  onClick={isFormEditable ? onClick : undefined}
                  disabled={!isFormEditable}
                  className={classNames(
                    'flex h-[80px] w-[80px] items-center justify-center rounded-full bg-white shadow-raised',
                    {
                      'cursor-pointer hover:bg-gray-100': isFormEditable,
                      'cursor-not-allowed': !isFormEditable
                    }
                  )}>
                  <HelixIcon
                    icon={CameraIcon}
                    className={classNames('h-8 w-8', {
                      'cursor-not-allowed': !isFormEditable
                    })}
                  />
                  <span className="sr-only">
                    Upload {isSecondary ? 'Secondary ' : ''}Logo
                  </span>
                </button>
              )}
            />
          </div>
        )}
      />
    </FormField>
  )

  const onSubmit = async (data: BrandingFormData) => {
    if (!isFormEditable || !currentUser?.id || !selectedEntity) {
      if (!selectedEntity)
        setApiErrors({ general: 'No entity selected for branding.' })
      return
    }

    clearErrors('logoFile')

    const hasPrimaryLogo =
      logoFile || (existingBranding?.logoUrl && !primaryLogoRemoved)
    if (!hasPrimaryLogo) {
      setError('logoFile', {
        type: 'manual',
        message: 'Primary logo is required.'
      })
      return
    }

    setApiErrors({})
    setSuccessMessage('')

    try {
      const isUpdate = !!existingBranding?.id
      const shouldRemoveSecondaryLogo =
        secondaryLogoRemoved && !secondaryLogoFile
      const shouldRemovePrimaryLogo = primaryLogoRemoved && !logoFile

      const { processedLogoFile, processedSecondaryLogoFile } =
        await processImagesWithCropping(
          logoFile,
          secondaryLogoFile,
          data.logoCropData,
          data.secondaryLogoCropData
        )

      let result
      if (isUpdate && !existingBranding?.inherited) {
        const updateInput = createBrandingInput(
          data,
          processedLogoFile,
          processedSecondaryLogoFile,
          shouldRemoveSecondaryLogo,
          shouldRemovePrimaryLogo
        )
        result = await updateBrandingMutation.mutateAsync({
          id: existingBranding.id,
          input: updateInput
        })

        if (result.updateBranding?.branding) {
          setSuccessMessage(
            `Branding updated successfully for ${selectedEntity.name}!`
          )
          setHasFormBeenModified(false)
        } else {
          setApiErrors({
            general: 'Failed to update branding. Please try again.'
          })
        }
      } else {
        const createInput = createNewBrandingInput(
          data,
          processedLogoFile,
          processedSecondaryLogoFile,
          selectedEntity,
          currentUser.id,
          shouldRemoveSecondaryLogo,
          shouldRemovePrimaryLogo
        )
        result = await createBrandingMutation.mutateAsync({
          input: createInput
        })

        if (result.createBranding?.branding) {
          setSuccessMessage(
            `Branding created successfully for ${selectedEntity.name}!`
          )
          setHasFormBeenModified(false)
        } else {
          setApiErrors({
            general: 'Failed to create branding. Please try again.'
          })
        }
      }
    } catch (error) {
      console.error('Error with branding:', error)
      setApiErrors({
        general: `An error occurred while ${
          existingBranding?.id ? 'updating' : 'creating'
        } the branding. Please try again.`
      })
    }
  }

  return (
    <>
      <form className="relative">
        {isDisabled && (
          <div className="absolute left-0 top-0 z-10 h-full w-full" />
        )}
        <div className={classNames({ 'blur-[3px]': isDisabled })}>
          {successMessage && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="helix-small--strong text-green-800">
                {successMessage}
              </p>
            </div>
          )}
          {apiErrors.general && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="helix-small--strong text-red-800">
                {apiErrors.general}
              </p>
            </div>
          )}
          <div className="flex w-full gap-[32px]">
            <div className="flex flex-1 flex-col space-y-6">
              <div className="flex gap-1">
                <FormField
                  label="Name"
                  error={formErrors.name?.message}
                  className="w-full">
                  <Input
                    fullWidth
                    size="sm"
                    placeholder={selectedEntity?.name || 'Name'}
                    disabled={!isFormEditable}
                    {...register('name', VALIDATION_RULES.name)}
                  />
                </FormField>
              </div>
              <h5 className="helix-heading text-gray-700">
                Logo, colors, and fonts
              </h5>
              <div className="flex items-center gap-4">
                {renderLogoUploader(
                  'logoFile',
                  'Primary Logo',
                  photoPreview,
                  true,
                  false
                )}
              </div>
              <div className="flex items-center gap-4">
                {renderLogoUploader(
                  'secondaryLogoFile',
                  'Secondary Logo',
                  secondaryPhotoPreview,
                  false,
                  true
                )}
              </div>
              <div className="flex gap-1">
                <FormField
                  label="Primary Color"
                  error={formErrors.primaryColor?.message}
                  className="w-full">
                  <Controller
                    name="primaryColor"
                    control={control}
                    rules={VALIDATION_RULES.primaryColor}
                    render={({ field }) => (
                      <ColorPicker
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="#000000"
                        disabled={!isFormEditable}
                        fullWidth
                        size="sm"
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className="relative flex gap-1">
                <FormField
                  label="Secondary Color"
                  error={formErrors.secondaryColor?.message}
                  className="w-full">
                  <Controller
                    name="secondaryColor"
                    control={control}
                    rules={VALIDATION_RULES.secondaryColor}
                    render={({ field }) => (
                      <ColorPicker
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="#000000"
                        disabled={!isFormEditable}
                        fullWidth
                        size="sm"
                      />
                    )}
                  />
                </FormField>
                <span className="absolute right-0 text-xs font-normal text-gray-500">
                  Optional
                </span>
              </div>
              <div className="flex gap-1">
                <FormField
                  label="Primary Font"
                  error={formErrors.primaryFont?.message}
                  className="w-full">
                  <Controller
                    name="primaryFont"
                    control={control}
                    rules={VALIDATION_RULES.primaryFont}
                    render={({ field }) => (
                      <FontSelect
                        aria-label="Primary Font"
                        value={field.value}
                        options={fontOptions}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isDisabled={!isFormEditable}
                        hasError={!!formErrors.primaryFont}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className="flex gap-1">
                <FormField
                  label="Secondary Font"
                  error={formErrors.secondaryFont?.message}
                  className="w-full">
                  <Controller
                    name="secondaryFont"
                    control={control}
                    rules={VALIDATION_RULES.secondaryFont}
                    render={({ field }) => (
                      <FontSelect
                        aria-label="Secondary Font"
                        value={field.value}
                        options={fontOptions}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isDisabled={!isFormEditable}
                        hasError={!!formErrors.secondaryFont}
                      />
                    )}
                  />
                </FormField>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex h-[160px] w-[165px] flex-col justify-between bg-gray-100 p-4 shadow-raised">
                <div className="flex flex-col gap-0.5 text-gray-700">
                  <span className="helix-small">Status:</span>
                  <span className="helix-small--strong">
                    {existingBranding
                      ? existingBranding.published
                        ? 'Active'
                        : 'Draft'
                      : 'Draft'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 text-gray-700">
                  <span className="helix-small">Created by:</span>
                  <span className="helix-small--strong">
                    {existingBranding?.creator?.name || '-'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 text-gray-700">
                  <span className="helix-small">Last Modified:</span>
                  <span className="helix-small--strong">
                    {existingBranding?.updatedAt
                      ? format(
                          new Date(existingBranding.updatedAt),
                          'MM/dd/yyyy'
                        )
                      : '-'}
                  </span>
                </div>
              </div>

              {selectedEntity.brandableType === 'Account' && (
                <div className="flex w-[165px] items-center justify-between">
                  <Tooltip content="Locking disallows lower-level admins to edit the values, ensuring brand consistency.">
                    <span className="ml-2 cursor-help text-xs font-medium text-gray-500">
                      Lock
                    </span>
                  </Tooltip>
                  <div className="helix-switch helix-switch--small">
                    <label>
                      <span className="helix-switch__label helix-small text-gray-600">
                        {formData.locked ? 'On' : 'Off'}
                      </span>
                      <Controller
                        name="locked"
                        control={control}
                        render={({ field }) => (
                          <input
                            type="checkbox"
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={!isFormEditable}
                          />
                        )}
                      />
                      <div className="helix-switch__control" />
                    </label>
                  </div>
                </div>
              )}

              {existingBranding?.editable === false &&
                existingBranding?.inherited === true && (
                  <div className="w-[165px]">
                    <p className="helix-small text-gray-500">
                      This {selectedEntity.brandableType.toLowerCase()} is
                      inheriting its branding from{' '}
                      {platformClient?.platformAccountName ||
                        'the main account'}
                      .
                    </p>
                  </div>
                )}
            </div>
          </div>
          <div className="helix-border-top mt-6 flex w-full items-center justify-end gap-3 pt-[16px]">
            <Button
              type="button"
              variant="primary"
              disabled={
                !isFormEditable ||
                !hasFormBeenModified ||
                createBrandingMutation.isLoading ||
                updateBrandingMutation.isLoading
              }
              onClick={handleSubmit(onSubmit)}>
              {createBrandingMutation.isLoading ||
              updateBrandingMutation.isLoading
                ? 'Saving...'
                : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </>
  )
}
